import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import ImageURL from "../../../config/ImageURL";
import "../../../styles/admin.css";
import Select from "react-select";

function LeadMagnetEdit() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/webp",
  ];

  const imageValidation = Yup.mixed()
    .nullable()
    .test("fileFormat", "Unsupported format", (value) => {
      return !value || (value && SUPPORTED_FORMATS.includes(value.type));
    })
    .test("fileSize", "File size is too large. Max 2MB.", (value) => {
      return !value || (value && value.size <= MAX_FILE_SIZE);
    });

  const validationSchema = Yup.object({
    title: Yup.string().required("*Please name field is required"),
    target_type: Yup.string().required("*Select a target Type"),
    image: imageValidation,
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      target_type: "",
      target_id: "",
      description: "",
      image: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      console.log("Form Data:", values);
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("title", values.title);
      formData.append("target_type", values.target_type);
      formData.append("description", values.description);
      formData.append("target_id", values.target_id);

      if (values.image) {
        formData.append("image", values.image);
      }

      setLoadIndicator(true);

      try {
        const response = await api.post(`admin/lead_magnet/update`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/leadmagnet");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error updating the campaign.";
        toast.error(errorMessage);
      }
      setLoadIndicator(false);
    },
  });

  useEffect(() => {
    // Fetch products
    api.get("admin/getAllProductList").then((response) => {
      const productData = response.data.data || [];
      setProducts(
        productData.map((product) => ({
          value: product.value,
          label: product.label,
        }))
      );
    });

    // Fetch categories
    api.get("admin/getAllCategoriesList").then((response) => {
      const categoryData = response.data.data || [];
      setCategories(
        categoryData.map((category) => ({
          value: category.value,
          label: category.label,
        }))
      );
    });

    // Fetch campaigns
    api.get("admin/getAllCampaignsList").then((response) => {
      const campaignData = response.data.data || [];
      setCampaigns(
        campaignData.map((campaign) => ({
          value: campaign.value,
          label: campaign.label,
        }))
      );
    });
  }, []);

  const getOptions = () => {
    switch (formik.values.target_type) {
      case "product":
        return products;
      case "category":
        return categories;
      case "campaign":
        return campaigns;
      default:
        return [];
    }
  };

  const getSelectedValue = () => {
    if (!formik.values.target_id) return null;
    return getOptions().find(
      (option) =>
        option.value.toString() === formik.values.target_id.toString()
    );
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`admin/lead_magnet`);
        const leadMagnetData = response.data.data;

        formik.setValues({
          title: leadMagnetData.title || "",
          target_type: leadMagnetData.target_type || "",
          target_id: leadMagnetData.target_id || "",
          description: leadMagnetData.description || "",
          image: null,
        });
        setPreviewImage(`${ImageURL}${leadMagnetData.image}`);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error Fetching Data";
        toast.error(errorMessage);
      }
      setLoading(false);
    };
    getData();
  }, []);

  const handleFileChange = (event) => {
    const file = event?.target?.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size is too large. Max 2MB.");
        event.target.value = null;
        formik.setFieldValue("image", null);
        return;
      }

      formik.setFieldValue("image", file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">Edit LeadMagnet</h1>
                  <Link to="/leadmagnet">
                    <button type="button" className="btn btn-light btn-sm">
                      <span>Back</span>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5">
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    placeholder="Title"
                    className={`form-control ${formik.touched.title && formik.errors.title
                        ? "is-invalid"
                        : ""
                      }`}
                    {...formik.getFieldProps("title")}
                    value={formik.values.title}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <div className="invalid-feedback">
                      {formik.errors.title}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Image
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".png,.jpeg,.jpg,.svg,.webp"
                    className={`form-control ${formik.touched.image && formik.errors.image
                        ? "is-invalid"
                        : ""
                      }`}
                    name="image"
                    onChange={handleFileChange}
                    onBlur={formik.handleBlur}
                  />
                  <p style={{ fontSize: "13px" }}>
                    Note: Maximum file size is 2MB. Allowed: .png, .jpg, .jpeg,
                    .svg, .webp.
                  </p>
                  {formik.touched.image && formik.errors.image && (
                    <div className="invalid-feedback">
                      {formik.errors.image}
                    </div>
                  )}

                  {previewImage && (
                    <div className="my-3">
                      <img
                        src={previewImage}
                        alt="Selected"
                        style={{ maxWidth: "100px", maxHeight: "100px" }}
                      />
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Tell Us Something...."
                    className={`form-control ${formik.touched.description && formik.errors.description
                        ? "is-invalid"
                        : ""
                      }`}
                    {...formik.getFieldProps("description")}
                    value={formik.values.description}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className="invalid-feedback">
                      {formik.errors.description}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Type</label>
                  <select
                    aria-label="Default select example"
                    className={`form-select ${formik.touched.target_type && formik.errors.target_type
                        ? "is-invalid"
                        : ""
                      }`}
                    {...formik.getFieldProps("target_type")}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldValue("target_id", "");
                    }}
                    value={formik.values.target_type}
                  >
                    <option value="">Select a Type</option>
                    <option value="default">Default</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="campaign">Campaign</option>
                  </select>
                  {formik.touched.target_type && formik.errors.target_type && (
                    <div className="invalid-feedback">
                      {formik.errors.target_type}
                    </div>
                  )}
                </div>

                {formik.values.target_type &&
                  formik.values.target_type !== "default" && (
                    <div className="col-md-6 col-12 file-input mb-5">
                      <label className="form-label">
                        {formik.values.target_type === "product" && "Product"}
                        {formik.values.target_type === "category" && "Category"}
                        {formik.values.target_type === "campaign" && "Campaign"}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={getOptions()}
                        value={getSelectedValue()}
                        onChange={(selectedOption) =>
                          formik.setFieldValue(
                            "target_id",
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        onBlur={formik.handleBlur}
                        name="target_id"
                        placeholder={`Search & select ${formik.values.target_type}...`}
                        isSearchable
                        className={`react-select ${formik.touched.target_id &&
                            formik.errors.target_id
                            ? "is-invalid"
                            : ""
                          }`}
                        classNamePrefix="select"
                      />
                      {formik.touched.target_id &&
                        formik.errors.target_id && (
                          <div className="invalid-feedback d-block">
                            {formik.errors.target_id}
                          </div>
                        )}
                    </div>
                  )}
              </div>
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Update
                </button>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default LeadMagnetEdit;